<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DiscoverItems from '@/pages/explore/DiscoverItems.vue';
import Sidebar from '@/pages/explore/Sidebar.vue';
import Tabs from '@/components/Tabs.vue';
import BuyBenchmarkCreditsModal from '@/components/modals/BuyBenchmarkCreditsModal.vue';
import { useExplorerStore } from '@/store/ExplorerStore';
import { useStorage } from '@/composables/use-storage';
import { getApiCreditsBalance } from '@/services/apiCreditsService.js';

const route = useRoute();
const router = useRouter();
const explorerStore = useExplorerStore();
const { isLoggedIn } = useStorage();

const creditsBalance = ref(null);
const showCreditsBanner = computed(() => {
  if (!isLoggedIn()) return false;
  if (!creditsBalance.value) return false;
  return Boolean(creditsBalance.value.low_balance) || Number(creditsBalance.value.balance_micro) === 0;
});

const showBuyCreditsModal = ref(false);

const openBuyCreditsModal = () => {
  showBuyCreditsModal.value = true;
};

const loadCreditsBalance = async () => {
  if (!isLoggedIn()) {
    creditsBalance.value = null;
    return;
  }
  try {
    creditsBalance.value = await getApiCreditsBalance();
  } catch {
    creditsBalance.value = null;
  }
};
const filterParams = ref({
    take: 12,
    skip: 0,
    delivery_method: 'featured'
});
const searchTerm = ref('');
const searchbarRef = ref(null);
const activeTab = ref('featured');
const sidebarListings = ref([]);

const tabs = ref([
    { label: 'Featured', value: 'featured', id: 'featured' },
    { label: 'Evals', value: 'eval', id: 'eval' },
    { label: 'Datasets', value: 'dataset', id: 'dataset' },
    { label: 'RFPs', value: 'rfp', id: 'rfp' },
    { label: 'All', value: 'all', id: 'all' },
]);

const TAB_MAPPING = {
    all: 'all',
    featured: 'featured',
    dataset: 'dataset',
    rfp: 'rfp',
    eval: 'eval'
};

const itemsCount = ref({
    featured: 0,
    dataset: 0,
    rfp: 0,
    eval: 0,
    '': 0,
    all: 0
});

const hasAppliedFilters = ref(false);

const KNOWN_FILTER_KEYS = [
    'modality',
    'use_case',
    'domain',
    'seller_badges',
    'eval_average_difficulty',
    'eval_model_name',
    'eval_score_range',
    'eval_total_tasks',
    'eval_cost_per_run',
    'eval_run_time_max',
    'size',
    'date_range',
    'price_range'
];

function hasSelectedFilters() {
    const filters = explorerStore.filters || {};
    const searchQuery = explorerStore.searchQuery || '';
    
    const hasArrayFilters = KNOWN_FILTER_KEYS.some(key => {
        const value = filters[key];
        return Array.isArray(value) && value.length > 0;
    });
    
    const hasStringFilters = KNOWN_FILTER_KEYS.some(key => {
        const value = filters[key];
        return typeof value === 'string' && value !== '';
    });
    
    return hasArrayFilters || hasStringFilters || searchQuery;
}

const normalizeTabValue = (type) => (type && TAB_MAPPING[type] ? type : 'featured');

const updateTabAndFilters = (tabValue) => {
    if (activeTab.value === tabValue) {
        return;
    }

    activeTab.value = tabValue;
    filterParams.value = {
        ...filterParams.value,
        delivery_method: tabValue === 'all' ? undefined : tabValue
    };

    nextTick(() => {
        updateTabIndicator();
    });
};

const shouldSkipTypeChange = (oldType) => {
    if (activeTab.value === 'all') {
        return true;
    }

    if (hasAppliedFilters.value || (searchTerm.value && searchTerm.value.trim())) {
        updateTabAndFilters('all');
        return true;
    }

    return oldType !== undefined;
};

const handleFilterUpdate = (filters) => {
    const filterObj = filters || {};
    
    const hasArrayFilters = KNOWN_FILTER_KEYS.some(key => {
        const value = filterObj[key];
        return Array.isArray(value) && value.length > 0;
    });
    
    const hasStringFilters = KNOWN_FILTER_KEYS.some(key => {
        const value = filterObj[key];
        return typeof value === 'string' && value !== '';
    });
    
    hasAppliedFilters.value = hasArrayFilters || hasStringFilters;
    
    const currentSearchQuery = route.query.search || searchTerm.value || '';
    
    filterParams.value = { 
        ...filters,
        take: 12,
        skip: 0,
        delivery_method: activeTab.value === 'all' ? undefined : activeTab.value
    };
    
    if (hasAppliedFilters.value && activeTab.value !== 'all') {
        activeTab.value = 'all';
        filterParams.value.delivery_method = undefined;
        
        const query = { ...route.query, type: undefined };
        if (currentSearchQuery.trim()) {
            query.search = currentSearchQuery.trim();
        }
        router.replace({ query });
        
        resetTabCount(activeTab.value);
        
        itemsCount.value.all = 0;
        itemsCount.value = { ...itemsCount.value };
    }
};

const handleFiltersApplied = () => {
    if (activeTab.value !== 'all') {
        activeTab.value = 'all';
        filterParams.value = { 
            ...filterParams.value,
            delivery_method: undefined
        };
        router.replace({ query: { ...route.query, type: undefined } });
    }
    hasAppliedFilters.value = true;
    
    itemsCount.value.all = 0;
    itemsCount.value = { ...itemsCount.value };
};

const handleFiltersCleared = () => {
    hasAppliedFilters.value = false;
    
    itemsCount.value = {
        featured: 0,
        dataset: 0,
        rfp: 0,
        eval: 0,
        '': 0,
        all: 0
    };
    
    const urlType = route.query.type;
    if (urlType && TAB_MAPPING[urlType]) {
        activeTab.value = urlType;
        filterParams.value = {
            take: 12,
            skip: 0,
            delivery_method: urlType === 'all' ? undefined : urlType
        };
    } else {
        activeTab.value = 'featured';
        filterParams.value = {
            take: 12,
            skip: 0,
            delivery_method: 'featured'
        };
        router.replace({ query: { ...route.query, type: 'featured' } });
    }
};

const resetTabCount = (tabValue) => {
    if (TAB_MAPPING[tabValue]) {
        itemsCount.value[TAB_MAPPING[tabValue]] = 0;
    }
    
    itemsCount.value = { ...itemsCount.value };
};



const updateItemsCount = (count) => {
    const countValue = parseInt(count) || 0;
    
    if (TAB_MAPPING[activeTab.value]) {
        itemsCount.value[TAB_MAPPING[activeTab.value]] = countValue;
    }

    if (searchTerm.value && searchTerm.value.trim()) {
        itemsCount.value[''] = countValue;
    }
    
    if (hasAppliedFilters.value) {
        itemsCount.value.all = countValue;
    }
    
    itemsCount.value = { ...itemsCount.value };
};

const handleListingsUpdated = (items) => {
    sidebarListings.value = Array.isArray(items) ? items : [];
};

watch(() => explorerStore.filters, () => {
    if (activeTab.value !== 'all') {
        resetTabCount(activeTab.value);
    }
    if (hasAppliedFilters.value) {
        itemsCount.value.all = 0;
        itemsCount.value = { ...itemsCount.value };
    }
}, { deep: true });

watch(() => explorerStore.searchQuery, () => {
    itemsCount.value[''] = 0;
    itemsCount.value = { ...itemsCount.value };
});

const handleTabChange = (tabValue) => {
    if (tabValue === activeTab.value) return;

    resetTabCount(activeTab.value);
    
    activeTab.value = tabValue;
    
    if (tabValue !== 'all') {
        hasAppliedFilters.value = false;
        filterParams.value = { 
            ...filterParams.value, 
            delivery_method: tabValue,
            modality: undefined,
            use_case: undefined,
            domain: undefined,
            size: undefined,
            date_range: undefined,
            price_range: undefined
        };
        router.replace({ query: { ...route.query, type: tabValue } });
    } else {
        filterParams.value = { 
            ...filterParams.value,
            delivery_method: undefined
        };
        router.replace({ query: { ...route.query, type: undefined } });
    }
    
    if (TAB_MAPPING[tabValue]) {
        itemsCount.value[TAB_MAPPING[tabValue]] = 0;
    }
    
    itemsCount.value = { ...itemsCount.value };
    
    updateTabIndicator();
};

const updateTabIndicator = () => {
    nextTick(() => {
        const activeTabElement = document.querySelector('.creator-profile__tab-btn--active');
        if (activeTabElement) {
            const tabsContainer = document.querySelector('.creator-profile__tabs');
            const tabLeft = activeTabElement.offsetLeft;
            const tabWidth = activeTabElement.offsetWidth;

            tabsContainer.style.setProperty('--tab-width', `${tabWidth}px`);
            tabsContainer.style.setProperty('--tab-left', `${tabLeft}px`);
        }
    });
};

const handleNavbarSearch = (searchValue) => {
    searchTerm.value = searchValue;
    explorerStore.setSearchQuery(searchValue);
    
    const query = { ...route.query };
    if (searchValue && searchValue.trim()) {
        query.search = searchValue.trim();
        if (activeTab.value !== 'all') {
            activeTab.value = 'all';
            filterParams.value = { 
                ...filterParams.value,
                delivery_method: undefined
            };
            router.replace({ query: { ...query, type: undefined } });
            
            resetTabCount(activeTab.value);
            
            nextTick(() => {
                updateTabIndicator();
            });
        } else {
            router.replace({ query });
        }
    } else {
        delete query.search;
        
        const urlType = route.query.type;
        if (urlType && TAB_MAPPING[urlType]) {
            activeTab.value = urlType;
            filterParams.value = {
                ...filterParams.value,
                delivery_method: urlType === 'all' ? undefined : urlType
            };
        } else if (!hasAppliedFilters.value) {
            activeTab.value = 'featured';
            filterParams.value = {
                ...filterParams.value,
                delivery_method: 'featured'
            };
            query.type = 'featured';
        }
        
        router.replace({ query });
        
        nextTick(() => {
            updateTabIndicator();
        });
    }
};


watch(() => route.query.search, (newSearchQuery) => {
    const searchValue = newSearchQuery || '';
    if (searchTerm.value !== searchValue) {
        searchTerm.value = searchValue;
        explorerStore.setSearchQuery(searchValue);
        
        if (searchValue.trim()) {
            if (activeTab.value !== 'all') {
                activeTab.value = 'all';
                hasAppliedFilters.value = false;
                filterParams.value = { 
                    ...filterParams.value,
                    delivery_method: undefined
                };
                router.replace({ query: { ...route.query, type: undefined } });
            }
        } else {
            itemsCount.value[''] = 0;
            itemsCount.value = { ...itemsCount.value };
        }
    }
}, { immediate: true });

watch(() => route.query.type, (newType, oldType) => {
    if (!newType && shouldSkipTypeChange(oldType)) {
        return;
    }

    updateTabAndFilters(normalizeTabValue(newType));
}, { immediate: true });

onMounted(async () => {
    const urlSearchQuery = route.query.search || '';
    const urlType = route.query.type;
    
    searchTerm.value = urlSearchQuery;
    if (urlSearchQuery !== '') {
        explorerStore.setSearchQuery(urlSearchQuery);
    }
    
    itemsCount.value = {
        featured: 0,
        dataset: 0,
        rfp: 0,
        eval: 0,
        '': 0,
        all: 0
    };
    
    if (urlType && TAB_MAPPING[urlType]) {
        activeTab.value = urlType;
        filterParams.value = { 
            ...filterParams.value,
            delivery_method: urlType === 'all' ? undefined : urlType
        };
    } else {
        activeTab.value = 'featured';
        filterParams.value = { 
            ...filterParams.value,
            delivery_method: 'featured'
        };
        router.replace({ query: { ...route.query, type: 'featured' } });
    }
    
    if (hasSelectedFilters()) {
        hasAppliedFilters.value = true;
    }
    
    updateTabIndicator();
    
    window.addEventListener('tab-changed', handleTabChanged);
    await loadCreditsBalance();
    if (route.query['buy-credits'] === '1' && isLoggedIn()) {
        showBuyCreditsModal.value = true;
        const nextQuery = { ...route.query };
        delete nextQuery['buy-credits'];
        router.replace({ query: nextQuery });
    }
});
    watch(
    () => route.path,
    (newPath) => {
        if (newPath === '/explore') {
            if (!route.query.type && activeTab.value !== 'all') {
                if (!hasSelectedFilters() && (!searchTerm.value || !searchTerm.value.trim())) {
                    activeTab.value = 'featured';
                    filterParams.value = { 
                        ...filterParams.value,
                        delivery_method: 'featured'
                    };
                }
            }
            
            if (hasSelectedFilters()) {
                hasAppliedFilters.value = true;
            }
            
            itemsCount.value = {
                featured: 0,
                dataset: 0,
                rfp: 0,
                eval: 0,
                '': 0,
                all: 0
            };
            
            updateTabIndicator();
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    window.removeEventListener('tab-changed', handleTabChanged);
});

const handleTabChanged = (event) => {
    if (event.detail.source === 'store' && event.detail.tab === 'all') {
        if (activeTab.value !== 'all') {
            activeTab.value = 'all';
            filterParams.value = { 
                ...filterParams.value,
                delivery_method: undefined
            };
            router.replace({ query: { ...route.query, type: undefined } });
            
            nextTick(() => {
                updateTabIndicator();
            });
        }
    }
};

</script>
<template>
            <section class="relative gap-4 flex h-full w-full min-w-0 p-4 2xl:container 2xl:mx-auto sm:py-[24px] overflow-y-auto" id="discover-items">
                <!-- Sidebar -->
                <Sidebar ref="searchbarRef" 
                         @filter-updated="handleFilterUpdate"
                         @filters-applied="handleFiltersApplied"
                         @filters-cleared="handleFiltersCleared"
                         @search="handleNavbarSearch"
                         :listings="sidebarListings" />
                
                <!-- Main Content -->
                <div class="flex-1 flex flex-col">
                    <div
                      v-if="showCreditsBanner"
                      class="mb-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                      <div>
                        <p class="text-white font-semibold">Run your own benchmarks</p>
                        <p class="text-white/70 text-sm">Buy Benchmark credits to create evals and run models on Portex.</p>
                      </div>
                      <button
                        type="button"
                        class="px-4 py-2 rounded-full bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 transition-colors"
                        @click="openBuyCreditsModal"
                      >
                        Buy credits
                      </button>
                    </div>
                    <div class="mb-2">
                        <Tabs :tabs="tabs" 
                              :activeTab="activeTab" 
                              @update:activeTab="handleTabChange" 
                              :itemsCount="itemsCount" />
                    </div>
                    <section class="flex-1 overflow-y-auto">
                        <DiscoverItems
                            :filters="filterParams"
                            :searchTerm="searchTerm"
                            :activeTab="activeTab"
                            @items-count="updateItemsCount"
                            @listings-updated="handleListingsUpdated"
                        />
                    </section>
                </div>
            </section>
            <BuyBenchmarkCreditsModal
              :show="showBuyCreditsModal"
              @close="showBuyCreditsModal = false"
              @success="loadCreditsBalance"
            />
</template>